/**
 * App feature toggles — DB `feature_flags` (see migration) + code defaults.
 * @typedef {{ is_enabled: boolean, launch_at?: string | null }} FeatureFlagRow
 */

/** @type {Record<string, boolean>} */
export const FEATURE_DEFAULTS_ENABLED = {
  trade: true,
  community: true,
  chat: true,
  emergency: true,
  visa_alerts: true,
  local_hospital: true,
  local_restaurant: true,
  local_supermarket: true,
  local_dental: true,
  local_laundry: true,
  local_repair: true,
  local_babysitter: true,
  local_translation: true,
  currency_exchange: true,
  jobs: true,
  real_estate: true,
  property_deal_jeonse: true,
  property_deal_sale: true,
  fortune: true,
  rental: true,
  professional: true,
  academy: true,
  restaurant: true,
  golf: true,
  medical_tourism: true,
  delivery: true,
  group_buy: true,
  visa_run: true,
  business_transfer: true,
  events: true,
  group_chat: true,
  groups: true,
  group_mna: false,
  live_commerce: true,
  crowdfunding: true,
  coworking: true,
  talent_market: true,
  luxury_used: true,
  flights: true,
  hotel: true,
  hospital: true,
  visa_service: true,
  auction: true,
  newsletter: true,
  point_market: true,
  deals: true,
  news: true,
  vehicles: true,
  language_exchange: true,
  surveys: true,
  map_search: true,
  local_beauty: true,
  local_cleaning: true,
  local_visa_agency: true,
  local_pet: true,
  local_fitness: true,
  local_korean_shop: true,
  local_wedding: true,
  local_settlement: true,
  local_insurance: true,
  local_remittance: true,
  local_airport_pickup: true,
  local_carpool: true,
  local_rider: true,
  local_schools: true,
  local_education_consult: true,
  local_activity: true,
  local_boat: true,
  local_photo: true,
  local_event_planner: true,
  local_digital: true,
  local_tours: true,
  local_interior: true,
  local_subscription: true,
  local_mentoring: true,
  local_return: true,
  recipes: true,
  experiences: true,
  property_auction: true,
  guide_market: true,
  sos: true,
  group_delivery: true,
  interpreter: true,
  business_verify: true,
  /** Hire posts require an owned, is_verified business (Albamon-style). DB can set false. */
  jobs_hire_require_verified: true,
  business_easy_onboarding: true,
  membership_inquiry: true,
  membership_booking: false,
  membership_payment: false,
  membership_escrow: false,
  membership_api_sync: false,
  membership_agency: false,
  golf_buddy: true,
  golf_club: true,
  ai_embeddings: false,
  semantic_search: false,
  audit_log_full: false,
  multi_region: false,
  recommendation_v2: false,
  idempotency_strict: false,
  soft_delete_enforce: false,
  points_system: false,
  point_packages_ui: false,
  booking_transfer: false,
  succession: false,
  listing_escrow: false,
  /** Stripe 카드결제 마스터 스위치 (광고구매/프리미엄/구인구직 프리미엄). env+key와 별개로 이게 꺼져있으면 결제 시도 자체를 막음. */
  payments_system: false,
};

/** @returns {Record<string, FeatureFlagRow>} */
export function buildDefaultFlagMap() {
  /** @type {Record<string, FeatureFlagRow>} */
  const out = {};
  for (const [k, is_enabled] of Object.entries(FEATURE_DEFAULTS_ENABLED)) {
    out[k] = { is_enabled, launch_at: null };
  }
  return out;
}

/**
 * @param {Record<string, FeatureFlagRow>|null|undefined} fromDb
 * @returns {Record<string, FeatureFlagRow>}
 */
export function mergeFeatureFlagMaps(fromDb) {
  const base = buildDefaultFlagMap();
  if (!fromDb || typeof fromDb !== 'object') return base;
  for (const k of Object.keys(base)) {
    const row = fromDb[k];
    if (row && typeof row.is_enabled === 'boolean') {
      base[k] = {
        is_enabled: row.is_enabled,
        launch_at: row.launch_at ?? null,
      };
    }
  }
  return base;
}

/**
 * @param {Record<string, FeatureFlagRow>} map
 * @param {string} key
 */
export function isFeatureEnabled(map, key) {
  if (!map || typeof map !== 'object') return false;
  const row = map[key];
  if (row && typeof row.is_enabled === 'boolean') return row.is_enabled;
  return FEATURE_DEFAULTS_ENABLED[key] ?? false;
}
